/**
 * Pre-neuter information letter — PDF component and field definitions.
 *
 * Auto-filled: clinic header, owner first name, patient name, owner phone.
 * Manual fields: arrival time, appointment date.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { CLINICS } from '@snootsvet/shared'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  formatArrivalTime,
  formatCalendarDate,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { InlineField } from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface PreNeuterLetterFields {
  arrivalTime: string
  appointmentDate: Date | null
}

export const defaultPreNeuterLetterFields: PreNeuterLetterFields = {
  arrivalTime: '',
  appointmentDate: null,
}

export { formatArrivalTime } from '@/frontend/features/printing/documents/blocks/format-utils'

export function introText(): string {
  return `Thank you for choosing Snoots to care for your pet's neutering! To ensure everything goes smoothly, here's some key information for the big day. Please read through carefully, and if you have any questions, don't hesitate to reach out.`
}

export const FASTING_INSTRUCTIONS = [
  'No food after 11 PM the night before and no breakfast on the morning of the procedure.',
  'Water is fine overnight but should be removed by 7 AM on the day.',
]

export function healthUpdatesText(patientName: string): string {
  return `Let us know if ${patientName} has experienced any recent health changes, surgeries or has been put on recent medication.`
}

export function contactInfoText(ownerPhone: string): string {
  return `We currently have ${ownerPhone} as your contact number, please check this is correct.`
}

export const OPTIONAL_EXTRAS = [
  'Pre-Op Blood Work is included for pets over 7 years of age, or are available as an optional extra for younger pets for £43.00.',
  'Want a nail trim or anal gland expression? Let our team know.',
  'If your pet needs a microchip please let us know!',
]

export function pickUpText(patientName: string): string {
  return `${patientName} will stay with us for the day. We'll message or call when ${patientName} is ready to go home.`
}

export const AFTERCARE_TEXT =
  "You'll receive detailed post-op instructions for a smooth recovery. Your pet will go home with a buster collar & some pain medication If you have questions or concerns, we're here to help."

export function closingText(patientName: string): string {
  return `Thank you again for trusting Snoots with ${patientName}'s care. We're looking forward to seeing you!`
}

const styles = StyleSheet.create({
  ...baseStyles,
})

export function PreNeuterLetterPdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: PreNeuterLetterFields
}) {
  const { patient, owner, clinic } = context
  // Generated for the owner, not a viewer, so the date order follows their region.
  const fmt = resolveDateFormat(null, owner.region)
  const arrivalTime = fields.arrivalTime ? formatArrivalTime(fields.arrivalTime) : undefined
  // A day picked in a date picker, not a moment — no zone may shift it.
  const appointmentDate = fields.appointmentDate
    ? formatCalendarDate(fields.appointmentDate, fmt, 'long')
    : undefined

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.text}>Dear {owner.firstName},</Text>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{introText()}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={{ ...styles.text, ...styles.bold }}>
            {/* Neutering procedures are carried out at Bounds Green, even though the
                letterhead shows the HQ contact details. */}
            The procedure will take place at {CLINICS.BOUNDS_GREEN.address}.
          </Text>
        </View>

        <View
          style={{
            ...styles.sectionGap,
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: mm(2),
            flexWrap: 'wrap',
          }}
        >
          <Text style={styles.bold}>Please arrive at</Text>
          <InlineField value={arrivalTime} minWidth={mm(20)} />
          <Text style={styles.bold}>AM on the</Text>
          <InlineField value={appointmentDate} minWidth={mm(35)} />
          <Text style={styles.bold}>for your admit appointment.</Text>
        </View>

        <Text style={styles.heading}>What You Need to Know:</Text>

        <Text style={{ ...styles.heading, fontSize: 10 }}>1. Fasting Instructions</Text>
        {FASTING_INSTRUCTIONS.map((item) => (
          <Text key={item} style={styles.listItem}>
            • {item}
          </Text>
        ))}

        <Text style={{ ...styles.heading, fontSize: 10 }}>2. Preparing for the Day</Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Health Updates:</Text> {healthUpdatesText(patient.name)}
        </Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Contact Info:</Text> {contactInfoText(owner.phone)}
        </Text>
        <View
          style={{
            ...styles.listItem,
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: mm(2),
            flexWrap: 'wrap',
          }}
        >
          <Text>
            • <Text style={styles.bold}>Arrival Time:</Text> Don't forget to arrive at
          </Text>
          <InlineField value={arrivalTime} minWidth={mm(20)} />
          <Text>for the admission appointment.</Text>
        </View>

        <Text style={{ ...styles.heading, fontSize: 10 }}>3. Optional Extras</Text>
        {OPTIONAL_EXTRAS.map((item) => (
          <Text key={item} style={styles.listItem}>
            • {item}
          </Text>
        ))}

        <Text style={{ ...styles.heading, fontSize: 10 }}>5. Post-Op Care</Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Pick-Up Time:</Text> {pickUpText(patient.name)}
        </Text>
        <Text style={styles.listItem}>
          • <Text style={styles.bold}>Aftercare:</Text> {AFTERCARE_TEXT}
        </Text>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{closingText(patient.name)}</Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>Best wishes,</Text>
          <Text style={styles.text}>The Snoots Team, North London</Text>
        </View>
      </Page>
    </Document>
  )
}
